import { PreviousGameVisitSuggestion } from '@src/pages/NewBoxPage/PreviousGameSuggestion';
import { PremiumGameUpgradePrompt } from './PremiumGameUpgradePrompt';
import { DeferredThemeReminder } from './DeferredThemeReminder';
import './BoxPage.css';
import { BoxDomainUnavailable } from '@src/routing/BoxDomainUnavailable';
import { peekParticipantAccess, clearParticipantAccess } from '@src/access/participantAccess';
import { BoxAppearanceProvider } from '@src/state/BoxAppearance';
import { HangingGameSignArtwork, HangingGameSignDetails } from './full/HangingGameSign';
import { useMobileBoxLayout } from './full/BoxLayouts';
import { BoxGameShell } from './full/BoxGameShell';
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BoxMode,
  type CardAccessClaimPreview,
  type CardAccessClaimResult,
} from '@lub0v/santa-utils';
import {
  Button,
  Panel,
  BoxShell,
  PageContent,
  Shell,
  Spinner,
  Stack,
  Typography,
} from '@src/components';
import {
  clearOrganizerAccess,
  peekOrganizerAccess,
  type StoredOrganizerAccess,
} from '@src/access/organizerAccess';
import { AccessCodes } from '@src/pages/BoxPage/AccessCodes';
import { BoxRecover } from '@src/pages/BoxPage/BoxRecover';
import { CardClaimPrompt } from '@src/components';
import { CardRecover } from '@src/pages/BoxPage/CardRecover';
import { FullBoxPage } from '@src/pages/BoxPage/full/FullBoxPage';
import { OrganizerDashboard } from '@src/pages/BoxPage/OrganizerDashboard';
import { ParticipantCard } from '@src/pages/BoxPage/ParticipantCard';
import { ParticipantSelect } from '@src/pages/BoxPage/ParticipantSelect';
import {
  clearCardAccessToken,
  isCardAccessUnavailable,
  joinKeyFromUrl,
  markCardAccessUnavailable,
  takeCardAccessToken,
} from '@src/pages/BoxPage/cardAccess';
import { apiUrlPath } from '@src/routing/urls';
import { boxExchangePath } from '@src/routing/routes';
import { useBoxRoute } from '@src/routing/useBoxRoute';
import { santaClient } from '@src/state/client';
import { useAppDispatch, useAppSelector } from '@src/state/hooks';
import { loadBox } from '@src/state/boxSlice';
import { loadSession } from '@src/state/sessionSlice';

/** The claim a personal link is asking the visitor to make. */
interface ClaimState {
  preview: CardAccessClaimPreview;
  cardKey: string;
  token: string;
}

/**
 * Everything behind `/box/:key`. Which screen that is depends on who the
 * visitor turns out to be, and the answer only arrives with the box: an
 * organizer gets the dashboard, a participant their card, and anyone else
 * either the list of names to claim from or a way back in.
 */
export function BoxPage() {
  const route = useBoxRoute();
  return (
    <BoxAppearanceProvider key={route?.key} boxKey={route?.key ?? ''}>
      <BoxPageContent />
    </BoxAppearanceProvider>
  );
}

function BoxPageContent() {
  const mobile = useMobileBoxLayout();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const route = useBoxRoute();
  const boxKey = route?.key ?? '';

  const { status, error, info, cards, card, ward } = useAppSelector((state) => state.box);
  const sessionStatus = useAppSelector((state) => state.session.status);

  const [join] = useState(() => joinKeyFromUrl());
  /*
   * Read once, as the page mounts. The token is taken out of the URL at the
   * same moment, so it never survives a copy of the address bar — and doing it
   * here rather than in an effect means the first render already knows whether
   * there is a claim to make.
   */
  const [token] = useState(() => (route?.cardKey ? takeCardAccessToken() : ''));
  const [claim, setClaim] = useState<ClaimState | null>(null);
  const [claimError, setClaimError] = useState<string | null>(null);
  /** A claim that has gone through, waiting on its codes being written down. */
  const [claimed, setClaimed] = useState<{
    mode: CardAccessClaimPreview['mode'];
    card: string;
    picture?: string | null;
    result: CardAccessClaimResult;
  } | null>(null);
  const [inspecting, setInspecting] = useState(
    () => Boolean(route?.cardKey && token) && !isCardAccessUnavailable(),
  );
  /** Join-new-card wizard — lives here so layout can change without remounting. */
  const [creatingCard, setCreatingCard] = useState(false);
  const [, refreshAccess] = useState(0);
  // Archived games are view-only, even when a card was never completed.
  const needsCardSetup = !info?.box.isArchived && Boolean(card?.needsCardSetup);
  const enteringCardSetup = !info?.box.isArchived && (creatingCard || needsCardSetup);
  const participantAccess = card && info?.isParticipant
    ? peekParticipantAccess(boxKey, card.randomKey) : undefined;
  const showingParticipantAccess = Boolean(
    participantAccess && (info?.box.mode === BoxMode.QUICK || !needsCardSetup),
  );
  const [organizerAccess, setOrganizerAccess] = useState<StoredOrganizerAccess | null>(() =>
    route && !route.cardKey ? (peekOrganizerAccess(route.key) ?? null) : null,
  );

  const refresh = useCallback(async () => {
    if (boxKey) {
      await dispatch(loadBox({ key: boxKey, join }));
    }
  }, [boxKey, dispatch, join]);

  /*
   * A personal link is inspected before anything else: it carries a one-time
   * token, and the box behind it is not readable until that token has been
   * spent. Everything else goes straight to loading the box.
   */
  useEffect(() => {
    const cardKey = route?.cardKey;
    if (!inspecting || !route || !cardKey) {
      return undefined;
    }

    let cancelled = false;
    void santaClient.inspectCardAccess({ key: route.key, cardKey, token }).then((result) => {
      if (cancelled) {
        return;
      }
      if (result.ok && result.data?.card) {
        setClaim({ preview: result.data, cardKey, token });
      } else {
        // Spent or expired: fall through to the box, which either shows the
        // card (this browser already claimed it) or the way back in.
        markCardAccessUnavailable();
        clearCardAccessToken();
      }
      setInspecting(false);
    });

    return () => {
      cancelled = true;
    };
  }, [inspecting, route, token]);

  /* The box is loaded once there is nothing left to claim. Always refetch
   * when this screen opens — Redux keeps the last box, so coming back after
   * archive would otherwise show the stale unarchived snapshot. */
  useEffect(() => {
    if (!boxKey || inspecting || claim) {
      return;
    }
    const timer = window.setTimeout(() => { void refresh(); }, 0);
    return () => window.clearTimeout(timer);
  }, [boxKey, claim, inspecting, refresh]);

  async function consumeClaim(state: ClaimState) {
    setClaimError(null);
    try {
      const result = await santaClient.consumeCardAccess({
        key: boxKey,
        cardKey: state.cardKey,
        token: state.token,
      });
      if (!result.ok || !result.data?.redirect) {
        setClaimError(result.error?.message ?? 'CARD_ACCESS_UNAVAILABLE');
        return;
      }

      clearCardAccessToken();
      const { data } = result;
      /*
       * A card claimed without an account lives in this browser and nowhere
       * else, so the codes that get it back are shown before the card itself —
       * the same stop v1 puts here. With an account there is nothing to save.
       * Quick draw skips codes and lands on the giftee reveal instead.
       */
      const isQuick = state.preview.mode === BoxMode.QUICK;
      if (data.recoveryCodes?.length && !isQuick) {
        setClaim(null);
        setClaimed({
          mode: state.preview.mode,
          card: state.preview.card.username,
          picture: state.preview.card.picture,
          result: data,
        });
        return;
      }

      // Leave the claim screen before navigating — otherwise a redirect to the
      // same card URL (quick draw) is a no-op and the button looks broken.
      setClaim(null);
      void dispatch(loadSession());

      const target = apiUrlPath(data.redirect);
      const here = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (target === here) {
        await refresh();
        return;
      }
      navigate(apiUrlPath(data.redirect));
    } catch {
      setClaimError('CARD_ACCESS_UNAVAILABLE');
    }
  }

  function body() {
    if (!route) {
      return (
        <Typography as="p" role="body/base" testId="box-missing">
          {t('box.notFound')}
        </Typography>
      );
    }

    if (claimed) {
      return (
        <AccessCodes
          kind="participant"
          subject={claimed.card}
          picture={claimed.picture}
          url={claimed.result.cardUrl}
          codes={claimed.result.recoveryCodes}
          onContinue={() => {
            void dispatch(loadSession());
            navigate(apiUrlPath(claimed.result.redirect));
          }}
        />
      );
    }

    if (claim) {
      return (
        <>
          {!mobile && (
            <header
              className="desktop-exchange-heading box-claim-heading"
              data-testid="box-claim-header"
            >
              <HangingGameSignArtwork
                name={claim.preview.boxName || ''}
                picture={claim.preview.boxPicture}
                logo={claim.preview.boxLogo}
              >
                <HangingGameSignDetails
                  organizer={
                    <>
                      {t('game.header.organizer')} {claim.preview.organizerName || '—'}
                    </>
                  }
                  drawn={claim.preview.isActiveDraw}
                  status={t(
                    claim.preview.isActiveDraw ? 'game.main.drawDone' : 'game.main.drawPending',
                  )}
                />
              </HangingGameSignArtwork>
            </header>
          )}
          <CardClaimPrompt
            boxName={claim.preview.boxName}
            organizerOnly={claim.preview.card.isAdmin === true}
            name={claim.preview.card.username}
            picture={claim.preview.card.picture}
            avatar={claim.preview.card.avatar}
            onClaim={() => consumeClaim(claim)}
            error={claimError}
          />
        </>
      );
    }

    // A named recovery route may initially have the shared invite's cached roster.
    // Wait for its access check before mounting the form: a rejected refresh clears
    // info and changes the shell, which would otherwise discard an entered code.
    const checkingCardAccess = Boolean(
      route.cardKey && !info?.isAdmin && card?.randomKey !== route.cardKey,
    );
    if (inspecting || status === 'idle' || (status === 'loading' && (!info || checkingCardAccess))) {
      return (
        <Stack gap={4} align="center" testId="box-loading">
          <Spinner size="medium" label={t('box.loading')} />
        </Stack>
      );
    }

    if (status === 'failed' || !info) {
      if (error === 'BOX_NOT_FOUND') {
        return (
          <Typography as="p" role="body/base" testId="box-missing">
            {t('box.notFound')}
          </Typography>
        );
      }
      /*
       * Anything else is "you cannot see this": a spent link, or a session
       * that does not own the box. A card link names who it was for, so the
       * ways back into that card can be offered; a bare box link cannot, and
       * starts from the list of names instead.
       */
      if (route.cardKey) {
        return <CardRecover boxKey={boxKey} cardKey={route.cardKey} token={token} />;
      }
      return <BoxRecover boxKey={boxKey} />;
    }

    /*
     * Recovery codes from create or rotate live in sessionStorage until this
     * screen is done. Organizers always stop here; full-game participants too.
     * Quick Draw participants only when they rotated codes from guest settings —
     * shared-claim codes have their own acknowledgement checkpoint below.
     */
    // Shared invites can still make the box readable after opening a named card.
    // A readable roster is not ownership: offer recovery instead of the picker.
    if (route.cardKey && !info.isAdmin && card?.randomKey !== route.cardKey) {
      return <CardRecover boxKey={boxKey} cardKey={route.cardKey} token={token} />;
    }

    if (showingParticipantAccess && participantAccess && card) {
      return <AccessCodes kind="participant" subject={card.username || ''}
        picture={card.picture} url={participantAccess.cardUrl} codes={participantAccess.recoveryCodes}
        onContinue={() => {
          clearParticipantAccess(boxKey, card.randomKey);
          refreshAccess((value) => value + 1);
          navigate(info.box.mode === BoxMode.QUICK
            ? apiUrlPath(participantAccess.redirect) : boxExchangePath(boxKey));
        }} />;
    }

    if (organizerAccess) {
      const isOrganizer = info.isAdmin;
      const isQuickDraw = info.box.mode === BoxMode.QUICK;
      const fromSettings = organizerAccess.rotatedFromSettings === true;

      if (!isOrganizer && isQuickDraw && !fromSettings) {
        clearOrganizerAccess(boxKey);
        setOrganizerAccess(null);
      } else {
        return (
          <AccessCodes
            kind={isOrganizer ? 'organizer' : 'participant'}
            subject={isOrganizer ? info.box.name : card?.username || info.box.name}
            picture={isOrganizer ? undefined : card?.picture}
            url={organizerAccess.cardUrl}
            codes={organizerAccess.recoveryCodes}
            onContinue={() => {
              const redirect = organizerAccess.redirect;
              clearOrganizerAccess(boxKey);
              setOrganizerAccess(null);
              if (!isOrganizer) {
                void dispatch(loadSession());
                navigate(apiUrlPath(redirect));
              }
            }}
          />
        );
      }
    }

    /*
     * Same split as v1: the box itself is the organizer's screen, and the two
     * /card shapes are the participant's. An organizer who follows a card link
     * lands on the list of names — and is told there that they cannot take one.
     */
    if (info.box.mode !== BoxMode.QUICK) {
      return (
        <FullBoxPage
          info={info}
          cards={cards}
          card={card}
          ward={ward}
          join={join}
          route={route}
          onChanged={refresh}
          creating={enteringCardSetup}
          onCreatingChange={setCreatingCard}
        />
      );
    }

    if (!route.card && info.isAdmin) {
      return <OrganizerDashboard info={info} cards={cards} onChanged={refresh} />;
    }

    if (info.isParticipant && card) {
      return <ParticipantCard info={info} card={card} ward={ward} onChanged={refresh} />;
    }

    return (
      <ParticipantSelect
        info={info}
        cards={cards}
        join={join}
        isOrganizer={Boolean(info.isAdmin)}
      />
    );
  }

  // Retain the claim's shell while game details load, so recovery-code
  // acknowledgement is not reset by moving the screen into a different shell.
  const shellBoxMode = claim?.preview.mode ?? claimed?.mode ?? info?.box.mode;
  const showingOrganizerAccess =
    Boolean(organizerAccess) &&
    (!info ||
      info.isAdmin ||
      info.box.mode !== BoxMode.QUICK ||
      organizerAccess?.rotatedFromSettings === true);
  const showingAccessCodes = Boolean(claimed) || showingOrganizerAccess || showingParticipantAccess;
  // Personal-link codes precede setup; shared-invite codes follow completed setup.
  const inCardCreate = Boolean(
    !showingAccessCodes &&
    !claim &&
    info &&
    info.box.mode !== BoxMode.QUICK &&
    enteringCardSetup,
  );
  const useFestiveShell = Boolean(route && !inCardCreate && shellBoxMode !== BoxMode.FULL);
  const unavailable = sessionStatus === 'failed' ||
    (status === 'failed' && !['BOX_NOT_FOUND', 'UNAUTHORIZED_FOR_BOX', 'UNAUTHORIZED', 'CARD_NOT_FOUND', 'CARD_ACCESS_UNAVAILABLE'].includes(error ?? ''));
  if (!route || (status === 'failed' && error === 'BOX_NOT_FOUND')) {
    return <BoxDomainUnavailable missing onRetry={() => { void refresh(); }} />;
  }
  const pageBody = unavailable ? (
    <div className="box-unavailable" data-testid="box-unavailable">
      <Panel className="box-unavailable__panel" padding="none" role="alert">
        <Stack gap={4} align="center">
          <Typography as="h1" role="title/section">{t('box.unavailableTitle')}</Typography>
          <Typography as="p" role="body/base">{t('box.unavailableDescription')}</Typography>
          <Button onClick={() => window.location.reload()}>{t('box.unavailableRetry')}</Button>
        </Stack>
      </Panel>
    </div>
  ) : (
    <>
      <PreviousGameVisitSuggestion info={info} enabled={status === 'ready' && sessionStatus === 'ready' && info?.box.key === boxKey && !showingAccessCodes && !inCardCreate && !claim} />
      <PremiumGameUpgradePrompt info={info} enabled={status === 'ready' && sessionStatus === 'ready' && info?.box.key === boxKey && !showingAccessCodes && !inCardCreate && !claim} />
      <DeferredThemeReminder info={info} enabled={status === 'ready' && !showingAccessCodes && !inCardCreate && !claim} />
      {body()}
    </>
  );

  if ((!info && status === 'failed' && route && !route.cardKey && !unavailable) || inCardCreate || (mobile && claim)) {
    return pageBody;
  }

  if (mobile && showingAccessCodes) {
    return (
      <Shell header={false} footer={false} landscape="soft">
        <PageContent width="dialog" className="access-codes-page" testId="box-page">
          {pageBody}
        </PageContent>
      </Shell>
    );
  }

  if (useFestiveShell) {
    return (
      <BoxShell
        header={!claim}
        width={showingAccessCodes ? 'dialog' : undefined}
        testId="box-shell"
      >
        {pageBody}
      </BoxShell>
    );
  }

  const showMainLandscape =
    !showingAccessCodes && info?.box.mode === BoxMode.FULL && route?.view === 'home';

  return (
    <BoxGameShell
      info={info}
      cards={cards}
      main={showMainLandscape}
      width={showingAccessCodes ? 'dialog' : undefined}
    >
      {pageBody}
    </BoxGameShell>
  );
}
